import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import { Navigation } from 'swiper/modules';
import { FaChevronRight } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import log from '../../utils/logger';
import api from '../../api/api';

const BrowseCategories = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const swiperRef = useRef(null);
  const [showArrows, setShowArrows] = useState(false);
  const [pulseLeft, setPulseLeft] = useState(false);
  const [pulseRight, setPulseRight] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await api.get('/buyer/category/getAllCategories');
        setCategories(res.data.response?.data || []);
      } catch (err) {
        log.error('Get categories error:', err.response?.data || err.message);
      }
    };
    fetchCategories();
  }, [setCategories]);

  // Detect if content overflows
  const updateOverflow = (swiper) => {
    if (!swiper) { return; }
    setShowArrows(swiper.wrapperEl.scrollWidth > swiper.width);
  };

  const handleScroll = (direction) => {
    if (!swiperRef.current) { return; }
    const swiper = swiperRef.current;

    if (direction === 'left') {
      swiper.slidePrev();
      setPulseLeft(true);
      setTimeout(() => setPulseLeft(false), 300);
    } else {
      swiper.slideNext();
      setPulseRight(true);
      setTimeout(() => setPulseRight(false), 300);
    }
  };

  return (
    <section className="pt-6 px-2 md:px-4 lg:px-6">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xl font-semibold text-[#000000DE]">{t('Browse Categories')}</h2>

        {showArrows && (
          <div className="hidden lg:flex gap-2">
            <button
              onClick={() => handleScroll('left')}
              className={`relative w-9 h-9 flex items-center justify-center rounded-md transition overflow-hidden
                ${swiperRef.current?.isBeginning ? 'bg-[#f5f5f5]' : 'bg-gray-200 hover:bg-[#ddd]'}`}
              disabled={swiperRef.current?.isBeginning}
            >
              {pulseLeft && <span className="absolute inset-0 rounded-md animate-pulse-button pointer-events-none bg-gray-400/60"></span>}
              <FaChevronRight className={`text-[20px] rotate-180 z-10 ${swiperRef.current?.isBeginning ? 'text-[#999]' : 'text-black'}`} />
            </button>

            <button
              onClick={() => handleScroll('right')}
              className={`relative w-9 h-9 flex items-center justify-center rounded-md transition overflow-hidden
                ${swiperRef.current?.isEnd ? 'bg-[#f5f5f5]' : 'bg-gray-200 hover:bg-[#ddd]'}`}
              disabled={swiperRef.current?.isEnd}
            >
              {pulseRight && <span className="absolute inset-0 rounded-md animate-pulse-button pointer-events-none bg-gray-400/60"></span>}
              <FaChevronRight className={`text-[20px] z-10 ${swiperRef.current?.isEnd ? 'text-[#999]' : 'text-black'}`} />
            </button>
          </div>
        )}
      </div>

      <div className="relative rounded-xl overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none lg:hidden absolute top-0 right-0 h-full w-8 bg-gradient-to-l from-white to-transparent z-10"
        />
        <Swiper
          slidesPerView="auto"
          spaceBetween={10}
          modules={[Navigation]}
          onBeforeInit={(swiper) => {
            swiper.params.navigation.prevEl = prevRef.current;
            swiper.params.navigation.nextEl = nextRef.current;
            swiperRef.current = swiper;
          }}
          onInit={(swiper) => updateOverflow(swiper)}
          onResize={(swiper) => updateOverflow(swiper)}
          navigation={{
            prevEl: prevRef.current,
            nextEl: nextRef.current,
          }}
          className="!pr-4"
        >
          {categories.map((category, index) => (
            <SwiperSlide key={index} className="!w-auto !flex-shrink-0">
              <div
                className="
                  flex flex-col items-center cursor-pointer gap-2
                  w-[80px] sm:w-[90px] md:w-[100px] lg:w-[110px] xl:w-[120px]
                  transition-all duration-300
                "
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/category/${category._id}`)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/category/${category._id}`); } }}
              >
                <img
                  src={category.imageUrl}
                  alt={i18n.language === 'fr' ? category.frName : category.enName}
                  loading="lazy"
                  className="h-[70px] md:h-[80px] lg:h-[90px] xl:h-[100px] object-contain"
                />
                <h3 className="text-xs font-medium leading-4 capitalize text-center truncate w-full">
                  {i18n.language === 'fr' ? category.frName : category.enName}
                </h3>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* Hide default Swiper arrows */}
      <style jsx>{`
        .swiper-button-prev, .swiper-button-next {
          display: none;
        }
      `}</style>
    </section>
  );
};

export default BrowseCategories;
